// reviewCards (0074): Review's cards over a served queue. Review's position
// (`?pos=`, views/Review.tsx) is an index into these cards, and every link into
// Review lands on the card holding its item (reviewCardHref,
// lib/reviewTarget.ts). A card holds one served item, in served order, its id
// that item's id, except that two or more waiting questions whose only option
// is the engine's confirm option share one card at the first one's place
// (0074, components/review/ConfirmGroupCard.tsx). This module reads only what
// the queue serves.

import type { components } from "../api/types";
import type { Queue } from "../hooks/useQueues";
import { readReopenedRecord } from "./exploratoryReading";
import { CONFIRM_PICK } from "./reviewLines";

type Node = components["schemas"]["Node"];

// A card: the served items it shows, in served order, and its id (unique among a
// queue's cards; the re-anchor, the data-swap hold and the keyboard guard record it).
export type ReviewCard = { id: string; nodes: readonly Node[] };

// The shared confirm card's id: one per queue, stable while the group exists
// (never an engine node id).
export const CONFIRM_GROUP_ID = "group:confirm";

// The parsed_picks entries a card keeps as options: exactly the entries
// readPicks (components/review/shared.tsx) keeps - an object whose label is a
// non-empty string - read here so no lib file imports a component.
function keptPicks(raw: unknown): Record<string, unknown>[] {
  if (!Array.isArray(raw)) return [];
  const kept: Record<string, unknown>[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue;
    const e = entry as Record<string, unknown>;
    if (typeof e.label === "string" && e.label !== "") kept.push(e);
  }
  return kept;
}

// isConfirmGroupMember: a question the queue serves as needs_user whose ONLY
// option is the confirm option (by pick_id, never its words), not awaiting the
// covered-by ruling and not reopened.
export function isConfirmGroupMember(node: Node, served: Queue): boolean {
  if (node.type !== "question" || node.status !== "needs_user") return false;
  const fields = (node.fields ?? {}) as Record<string, unknown>;
  const picks = keptPicks(fields.parsed_picks);
  if (picks.length !== 1 || picks[0].pick_id !== CONFIRM_PICK) return false;
  if (served.coveredByRulingNodeIds.has(node.id)) return false;
  return readReopenedRecord(fields) === null;
}

// One card per served item, in served order, its id the item's id; [] for no
// data. Two or more members (isConfirmGroupMember) share one card,
// CONFIRM_GROUP_ID, at the first member's place, holding them in served order;
// one member alone keeps its own card.
export function reviewCards(served: Queue | undefined): ReviewCard[] {
  if (served === undefined) return [];
  const members = served.items.filter((n) => isConfirmGroupMember(n, served));
  if (members.length < 2) return served.items.map((n) => ({ id: n.id, nodes: [n] }));
  const memberIds = new Set(members.map((n) => n.id));
  const cards: ReviewCard[] = [];
  let grouped = false;
  for (const n of served.items) {
    if (!memberIds.has(n.id)) {
      cards.push({ id: n.id, nodes: [n] });
    } else if (!grouped) {
      cards.push({ id: CONFIRM_GROUP_ID, nodes: members });
      grouped = true;
    }
  }
  return cards;
}

// The cards on which anything waits: a card waits while any item on it waits
// (waitingNodeIds). Review's forward moves read it; the tab counts stay per item.
export function waitingCardIds(cards: readonly ReviewCard[], waitingItemIds: ReadonlySet<string>): Set<string> {
  return new Set(cards.filter((c) => c.nodes.some((n) => waitingItemIds.has(n.id))).map((c) => c.id));
}
